import React, { useState, useCallback } from "react";
import { StyleSheet, ScrollView } from "react-native";
import { View, Wrapper, Text, Card } from "@components/atoms/Themed";
import ErrorDialog from "@components/molecules/dialog/ErrorDialog";
import Toast from "@components/atoms/Toast";
import KeyboardAvoidingView from "@components/molecules/KeyboardAvoidingView";
import { graphql, useFragment, useMutation } from "react-relay/hooks";
import { SnsAssociationTiktokOAuth$key } from "@generated/SnsAssociationTiktokOAuth.graphql";
import Spacer from "@components/atoms/Spacer";
import GradientButton from "@components/atoms/GradientButton";
import Divider from "@components/atoms/Divider";
import SNSIcon from "@components/atoms/SNSIcon";
import OAuth from "@components/molecules/OAuth";
import Colors from "@constants/Colors";
import Fonts from "@constants/Fonts";
import TtgoCreatorSwitch from "@components/molecules/TtgoCreatorSwitch";
import { TtgoStatus } from "@components/atoms/TtgoStatusBadge";

const tiktokQuery = graphql`
  fragment SnsAssociationTiktokOAuth on Influencer {
    linkingTiktok
    profile {
      isTiktokGoCreator
      tiktokGoStatus
      tiktokGoUniqueId
    }
  }
`;

const applyTtgoMutationGraphql = graphql`
  mutation SnsAssociationTiktokOAuthApplyTtgoMutation(
    $input: ApplyTiktokGoCreatorMutationInput!
  ) {
    applyTiktokGoCreator(input: $input) {
      ... on Influencer {
        id
        profile {
          isTiktokGoCreator
          tiktokGoStatus
          tiktokGoUniqueId
        }
      }
      ... on UserError {
        message
      }
    }
  }
`;

export default function SnsAssociationTiktokOAuth({
  tiktokFragment,
}: {
  tiktokFragment: SnsAssociationTiktokOAuth$key;
}) {
  const { linkingTiktok, profile } = useFragment(tiktokQuery, tiktokFragment);
  const [loading, setLoading] = useState<boolean>(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [commitApplyTtgo] = useMutation(applyTtgoMutationGraphql);

  // 却下後もisTiktokGoCreatorはtrueのままなので、申請スイッチはオフで表示する。
  const initialTtgoApply =
    profile.isTiktokGoCreator && profile.tiktokGoStatus !== "rejected";
  const initialTtgoUniqueId = profile.tiktokGoUniqueId ?? "";
  const [ttgoApply, setTtgoApply] = useState<boolean>(initialTtgoApply);
  const [ttgoUniqueId, setTtgoUniqueId] = useState<string>(initialTtgoUniqueId);

  const ttgoDirty =
    ttgoApply !== initialTtgoApply || ttgoUniqueId !== initialTtgoUniqueId;

  const saveTtgo = useCallback(async (): Promise<boolean> => {
    if (!ttgoDirty) return true;
    return new Promise<boolean>((resolve) => {
      commitApplyTtgo({
        variables: {
          input: {
            apply: ttgoApply,
            tiktokGoUniqueId:
              ttgoApply && ttgoUniqueId.trim() !== ""
                ? ttgoUniqueId.trim()
                : null,
          },
        },
        onCompleted(response) {
          const result = (
            response as { applyTiktokGoCreator?: { message?: string } }
          ).applyTiktokGoCreator;
          if (result != null && typeof result.message === "string") {
            setError(result.message);
            resolve(false);
            return;
          }
          resolve(true);
        },
        onError() {
          setError("TikTokGOクリエイター申請の保存に失敗しました");
          resolve(false);
        },
      });
    });
  }, [commitApplyTtgo, ttgoApply, ttgoDirty, ttgoUniqueId]);

  const save = useCallback(async () => {
    if (ttgoApply && ttgoUniqueId.trim() === "") {
      setError("TikTokGO ユニークIDを入力してください");
      return;
    }

    setLoading(true);
    const ok = await saveTtgo();
    if (ok) {
      setMessage("保存しました");
    }
    setLoading(false);
  }, [saveTtgo, ttgoApply, ttgoUniqueId]);

  return (
    <View style={styles.container}>
      <Spacer height={8} />
      <KeyboardAvoidingView>
        <ScrollView>
          <Wrapper>
            <Card style={styles.content}>
              <View style={styles.sns}>
                <SNSIcon name="tiktok" style={styles.socialIcon} />
                <Spacer width={8} />
                <View style={styles.title}>
                  <Text style={styles.text}>TikTok</Text>
                </View>
                <Spacer width={24} />
                {linkingTiktok === true ? (
                  <View style={styles.link}>
                    <Text style={styles.linked}>連携済み</Text>
                  </View>
                ) : (
                  <OAuth
                    action="Linking"
                    callbackUrl="sns?tab=tiktok"
                    type="Tiktok"
                  >
                    <View style={[styles.link, styles.button]}>
                      <Text style={styles.buttonText}>連携する</Text>
                    </View>
                  </OAuth>
                )}
              </View>
            </Card>

            <Spacer height={16} />

            <Card style={styles.content}>
              <TtgoCreatorSwitch
                apply={ttgoApply}
                isTiktokGoCreator={profile.isTiktokGoCreator}
                onChangeApply={setTtgoApply}
                onChangeUniqueId={setTtgoUniqueId}
                tiktokGoStatus={profile.tiktokGoStatus as TtgoStatus}
                uniqueId={ttgoUniqueId}
              />
            </Card>
          </Wrapper>
        </ScrollView>
      </KeyboardAvoidingView>

      <Divider />
      <Card style={styles.footer}>
        <GradientButton
          disabled={!ttgoDirty}
          loading={loading}
          onPress={save}
          title="保存する"
        />
      </Card>

      {message !== null && (
        <Toast message={message} onClose={() => setMessage(null)} />
      )}
      {error !== null && (
        <ErrorDialog message={error} onClose={() => setError(null)} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  footer: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  content: {
    paddingHorizontal: 16,
    paddingVertical: 24,
  },
  sns: {
    flexDirection: "row",
    alignItems: "center",
  },
  title: {
    flex: 1,
  },
  text: {
    ...Fonts.xlr100,
    color: Colors.black,
  },
  socialIcon: {
    width: 24,
    height: 24,
  },
  buttonText: {
    ...Fonts.lb130,
    color: Colors.white,
  },
  link: {
    width: 64,
    height: 32,
    borderRadius: 4,
    alignItems: "center",
    justifyContent: "center",
  },
  button: {
    backgroundColor: Colors.green,
  },
  linked: {
    color: Colors.green,
    ...Fonts.xlb100,
  },
});
